/* CaptureRenderer.js */

/* global Slick */
"use strict";
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const ButtonControl = uiCore.Control.Action.Button;
const CreateCommand = require("./veil/CreateEditCommand/CreateEditCommandComponent");
const CreateEditRedaction = require("./veil/CreateEditRedaction/CreateEditRedactionComponent");
const createEditExclusion = require("./veil/CreateEditExclusion/CreateEditExclusionComponent");
const formatter = require("./cellFormaters");
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const InputTextEditor = require("./cellEditors/InputTextEditor");
const CheckBoxEditor = require("./cellEditors/CheckBoxEditor");
// const {withDialog} = require("./cellEditors/CommonEditor");
const moment = require("moment-timezone");
const ImportComponent = require("./veil/import/ImportScreenComponent");
const ImportSummaryComponent = require("./veil/import/ImportSummaryComponent");
const ExportSummaryComponent = require("./veil/export/ExportSummaryComponent");

/**
 * class CaptureRenderer
 *
 * @listens CaptureRenderer#event:DataUpdated
 *
 */
class CaptureRenderer {
    /**
     * @param {CaptureRenderer} model
     * @param {UiUtils} uiUtils
     */
    constructor(model, uiUtils) {
        this._model = model;
        this._uiUtils = uiUtils;
        this._rootElem = document.createElement("capture-gl");
        this._rootElem.component = this._model;
        const heading = document.createElement("h1");
        heading.innerText = "Discovery & Capture";
        this._rootElem.appendChild(heading);
        this._rootElemContainer = document.createElement("div");
        this._rootElemContainer.classList.add("container");
        this._rootElem.appendChild(this._rootElemContainer);
        this._discoveryCommandsContainer = document.createElement("div");
        this._discoveryCommandsContainer.classList.add("discovery-commands-container");
        this._networkDiscoveryContainer = document.createElement("div");
        this._networkDiscoveryContainer.classList.add("network-discovery-container");
        this._exclusionsHeaderElem = document.createElement("h1");
        this._exclusionsHeaderElem.innerText = "Exclusions List";
        this._commandSetHeaderElem = document.createElement("h1");
        this._commandSetHeaderElem.innerText = "Capture Command Sets";
        this._redactionHeaderElem = document.createElement("h1");
        this._redactionHeaderElem.innerText = "Redaction Expressions";
        this._allowedHeaderElem = document.createElement("h1");
        this._allowedHeaderElem.innerText = "Allowed Commands";
        this._discoveryHeaderElem = document.createElement("h1");
        this._discoveryHeaderElem.innerText = "Options";
        this._slickTableContainer = document.createElement("capture-table");
        this._slickWrapper = document.createElement("capture-wrapper");
        this._fileFactory = this._model._fileFactory;

        this._importCommandSetNames = [];
        this._importRegexNames = [];
        this._importData = [];

        this._onCellChangeAction = this._onCellChangeAction.bind(this);

        // this._showHideStandardSets = new CheckboxControl({
        //     darkTheme: true,
        //     labelShown: true,
        //     type: "toggle",
        //     label: "Show Gluware Standard",
        //     title: "Show Gluware Standard",
        //     value: false,
        // });
        this._showHideDisabledSets = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            type: "toggle",
            label: "Show Disabled",
            title: "Show Disabled",
            value: false,
        });
        this._checkboxContainer = document.createElement("div");
        this._checkboxContainer.classList.add("toggle-checkbox-container");
        // this._checkboxContainer.appendChild(this._showHideStandardSets._renderer._rootElem);
        this._checkboxContainer.appendChild(this._showHideDisabledSets._renderer._rootElem);
        // this._slickWrapper.appendChild(this._checkboxContainer);

        this._slickWrapper.appendChild(this._slickTableContainer);
        this._slickTable = undefined;
        this._slickRdTableContainer = document.createElement("redaction-table");
        this._slickRdWrapper = document.createElement("redaction-wrapper");
        this._slickRdWrapper.appendChild(this._slickRdTableContainer);
        this._slickRdTable = undefined;

        this._slickAllowedTableContainer = document.createElement("allowed-table");
        this._slickAllowedWrapper = document.createElement("allowed-wrapper");
        this._slickAllowedWrapper.appendChild(this._slickAllowedTableContainer);
        this._slickAllowedTable = undefined;

        this._slickExclusionsTableContainer = document.createElement("exclusions-table");
        this._slickExclusionsWrapper = document.createElement("exclusions-wrapper");

        this._slickExclusionsWrapper.appendChild(this._slickExclusionsTableContainer);
        this._slickExclusionsTable = undefined;

        this._onSort = _.debounce(this._onSort.bind(this), 0);
        this._onSortRedaction = _.debounce(this._onSortRedaction.bind(this), 0);
        this._onSortAllowed = _.debounce(this._onSortAllowed.bind(this), 0);
        this._dataUpdateExclusions = this._dataUpdateExclusions.bind(this);
        this._controlButtonsContainer = document.createElement("div");
        this._controlButtonsContainer.classList.add("control-buttons");
        this._importButton = new ButtonControl({
            label: "Import",
            title: "Import",
            darkTheme: true,
        });
        this._exportButton = new ButtonControl({
            label: "Export",
            title: "Export",
            darkTheme: true,
        });
        this._controlButtonsContainer.appendChild(this._importButton.renderer.getRootElement());
        this._controlButtonsContainer.appendChild(this._exportButton.renderer.getRootElement());
        this._columns = [
            {
                id: "enabled",
                width: 100,
                name: "Enabled",
                field: "enabled",
                toolTip: "enabled",
                formatter: formatter.checkboxFormatter,
                resizable: false,
                sortable: true,
            },
            {
                id: "commandName",
                width: 77,
                name: "Name",
                field: "commandName",
                toolTip: "Name",
                formatter: formatter.defaultFormatter,
                resizable: true,
                sortable: true,
            },
            {
                id: "vendor",
                width: 20,
                name: "Vendor",
                field: "vendor",
                toolTip: "Vendor Name",
                formatter: formatter.labelValuePairFormatter,
                resizable: true,
                sortable: true,
            },
            {
                id: "os",
                width: 25,
                name: "OS",
                field: "os",
                toolTip: "OS Name",
                formatter: formatter.defaultFormatter,
                resizable: true,
                sortable: true,
            },
            {
                id: "type",
                width: 10,
                name: "Type",
                field: "type",
                toolTip: "Type",
                formatter: formatter.labelValuePairFormatter,
                // To be changed in the map at platform level
                // labelValueMap: this._model._CommandSetEntityDef.getDataTypeLabels(),
                labelValueMap: {
                    DRIFT_AND_AUDIT: "Configuration",
                    AUDIT_ONLY: "Operational",
                },
                resizable: true,
                sortable: true,
            },
            {
                id: "basicMode",
                width: 100,
                name: "Standard",
                field: "basicMode",
                toolTip: "Standard",
                formatter: formatter.checkboxFormatter,
                resizable: false,
                sortable: true,
            },
            {
                id: "deviceFilter",
                width: 100,
                name: "Filter",
                field: "deviceFilter",
                toolTip: "Has a Device Filter",
                formatter: formatter.checkboxFormatter,
                resizable: false,
                sortable: true,
            },
            {
                id: "actions",
                width: 103,
                name: "Actions",
                field: "actions",
                toolTip: "Actions",
                formatter: formatter.actionButtonsFormatter,
                resizable: false,
            },
        ];
        this._columnsRedaction = [
            {
                id: "redactionName",
                width: 127,
                name: "Name",
                field: "redactionName",
                resizable: true,
                toolTip: "Redaction Name",
                sortable: true,
            },
            {
                id: "type",
                width: 49,
                name: "Type",
                field: "type",
                resizable: true,
                toolTip: "Type",
                sortable: true,
            },
            {
                id: "actions",
                width: 93,
                name: "Actions",
                field: "actions",
                toolTip: "Actions",
                resizable: false,
                formatter: formatter.actionButtonsRedactionFormatter,
            },
        ];

        this._exclusionColumns = [
            {
                id: "type",
                width: 265,
                name: "Type",
                field: "type",
                resizable: false,
                toolTip: "Type",
                sortable: false,
            },
            {
                id: "value",
                width: 300,
                name: "Value",
                field: "value",
                resizable: false,
                toolTip: "Value",
                sortable: false,
                formatter: formatter.exclusionValueFormatter,
            },
            {
                id: "description",
                width: 500,
                name: "Description",
                field: "description",
                resizable: false,
                toolTip: "Description",
                sortable: false,
            },
            {
                id: "actions",
                width: 93,
                name: "Actions",
                field: "actions",
                toolTip: "Actions",
                resizable: false,
                formatter: formatter.actionButtonsExclusionsFormatter,
                headerCssClass: "centered",
                cssClass: "centered",
            },
        ];

        this._columnsAllowed = [
            {
                id: "allowedName",
                width: 70,
                name: "Command",
                field: "allowedName",
                resizable: true,
                toolTip: "Raw Command",
                sortable: true,
                editor: InputTextEditor,
            },
            {
                id: "group1",
                width: 49,
                name: "Read Group",
                field: "group1",
                resizable: true,
                toolTip: "Is in the Read group",
                formatter: formatter.checkboxFormatter,
                sortable: true,
                editor: CheckBoxEditor,
            },
            {
                id: "group2",
                width: 49,
                name: "Write Group",
                field: "group2",
                resizable: true,
                toolTip: "Is in the Write group",
                formatter: formatter.checkboxFormatter,
                sortable: true,
                editor: CheckBoxEditor,
            },
            {
                id: "nonCapture",
                width: 49,
                name: "Exclude from Snapshot",
                field: "nonCapture",
                resizable: true,
                toolTip: "Response will not be stored in the captured Snapshot",
                formatter: formatter.checkboxFormatter,
                sortable: true,
                editor: CheckBoxEditor,
            },
        ];

        if (this._model.isAuthorizedToManageAllowedCommands()) {
            this._columnsAllowed.push({
                id: "actions",
                width: 75,
                name: "Actions",
                field: "actions",
                toolTip: "Actions",
                resizable: false,
                formatter: formatter.actionButtonsAllowedFormatter,
            });
        }

        this._tableOptions = {
            editable: true,
            enableCellNavigation: true,
            enableAsyncPostRender: true,
            forceFitColumns: true,
            autoHeight: true,
            autoEdit: true,
            resizable: true,
            rowHeight: 45,
        };
        this._enableHostNamingCheckBox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            type: "toggle",
            label: "Update device name with discovered hostname",
            title: "Update all device names with discovered hostnames for the current organization",
        });
        this._enableVerboseLogging = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            type: "toggle",
            label: "Enable verbose logging",
            title: "Enable verbose logging",
        });
        this._addNewRowButton = new ButtonControl({
            label: "Add Capture Command Set +",
            active: true,
        });
        this._addNewRedactionRowButton = new ButtonControl({
            label: "Add Redaction Expression +",
            active: true,
        });
        /*
        this._resetAllowedGridButton = new ButtonControl({
            label: "Reset to default list",
            active: true,
        });

         */
        this._addNewAllowedGridButton = new ButtonControl({
            label: "Add Allowed Command +",
            active: true,
        });

        this._addNewExclusionButton = new ButtonControl({
            label: "Add Exclusion +",
            active: true,
        });

        this._clickHandler = this._clickHandler.bind(this);
        this._fitTableSize = this._fitTableSize.bind(this);
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    shutdown() {
        this._unregisterListeners();
        this._uiUtils.clearMessage();
        if (this._slickTable && this._slickTable.destroy) {
            this._slickTable.destroy();
        }
        return Promise.all([
            this._model._muf.hideDisplayed(),
            this._addNewRowButton.shutdown(),
            this._addNewRedactionRowButton.shutdown(),
            this._addNewAllowedGridButton.shutdown(),
            //this._resetAllowedGridButton.shutdown(),
            this._addNewExclusionButton.shutdown(),
            this._importButton.shutdown(),
            this._exportButton.shutdown(),
        ]).then(() => {
            this._addNewRowButton = undefined;
            this._addNewRedactionRowButton = undefined;
            this._addNewAllowedGridButton = undefined;
            this._addNewExclusionButton = undefined;
            //this._resetAllowedGridButton = undefined;
            this._commandSetHeaderElem.remove();
            this._commandSetHeaderElem = undefined;
            this._exclusionsHeaderElem.remove();
            this._exclusionsHeaderElem = undefined;
            this._redactionHeaderElem.remove();
            this._redactionHeaderElem = undefined;
            this._discoveryHeaderElem.remove();
            this._discoveryHeaderElem = undefined;
            this._slickTableContainer.remove();
            this._slickTableContainer = undefined;
            this._slickWrapper.remove();
            this._slickWrapper = undefined;
            this._slickTable = undefined;
            this._slickRdTableContainer.remove();
            this._slickRdTableContainer = undefined;
            this._slickRdWrapper.remove();
            this._slickRdWrapper = undefined;
            this._slickRdTable = undefined;
            this._columns = undefined;
            this._columnsRedaction = undefined;
            this._controlButtonsContainer.remove();
            this._controlButtonsContainer = undefined;
            this._importButton = undefined;
            this._exportButton = undefined;
            this._model = undefined;
            this._rootElem.remove();
            this._rootElem = undefined;
            this._uiUtils = undefined;
            this._fileFactory = undefined;
        });
    }

    _registerListeners() {
        // this._showHideStandardSets.on(
        //     "UserValue",
        //     (this._showStandardSetsClick = this._showStandardSetsClick.bind(this))
        // );
        this._showHideDisabledSets.on(
            "UserValue",
            (this._showDisabledSetsClick = this._showDisabledSetsClick.bind(this))
        );

        this._addNewRowButton.on(
            "UserClick",
            (this._addNewRowAction = this._addNewRowAction.bind(this))
        );

        this._exportButton.on("UserClick", (this._onExportClick = this._onExportClick.bind(this)));
        this._importButton.on("UserClick", (this._onImportClick = this._onImportClick.bind(this)));

        this._addNewRedactionRowButton.on(
            "UserClick",
            (this._addNewRedactionRowAction = this._addNewRedactionRowAction.bind(this))
        );
        this._addNewAllowedGridButton.on(
            "UserClick",
            (this._addNewAllowedGridAction = this._addNewAllowedGridAction.bind(this))
        );
        this._addNewExclusionButton.on(
            "UserClick",
            (this._addNewExclusionAction = this._addNewExclusionAction.bind(this))
        );
        /*
        this._resetAllowedGridButton.on(
            "UserClick",
            (this._resetAllowedGridAction = this._resetAllowedGridAction.bind(this))
        );

         */
        if (this._slickAllowedTable) {
            this._slickAllowedTable.onCellChange.subscribe(this._onCellChangeAction);
        }
        this._enableHostNamingCheckBox.on(
            "UserValue",
            this._onEnableHostNamingCheckBoxChange.bind(this)
        );
        this._enableVerboseLogging.on(
            "UserValue",
            this._onEnableVerboseLoggingCheckBoxChange.bind(this)
        );
        document.addEventListener("click", this._clickHandler);
        window.addEventListener("resize", this._fitTableSize);
        this._model.on("DataUpdated", (this._dataUpdated = this._dataUpdated.bind(this)));
        this._model.on("RdDataUpdated", (this._dataUpdatedRd = this._dataUpdatedRd.bind(this)));
        this._model.on(
            "AllowedDataUpdated",
            (this._dataUpdatedAllowed = this._dataUpdatedAllowed.bind(this))
        );
        this._model.on("ExclusionsDataUpdated", this._dataUpdateExclusions);
        this._slickTable.onSort.subscribe(this._onSort);
        this._slickRdTable.onSort.subscribe(this._onSortRedaction);
        this._slickAllowedTable.onSort.subscribe(this._onSortAllowed);
        this._model.on("OrgChange", (this._orgChange = this._orgChange.bind(this)));
    }

    _unregisterListeners() {
        // this._showHideStandardSets.removeListener("UserValue", this._showStandardSetsClick);
        this._showHideDisabledSets.removeListener("UserValue", this._showDisabledSetsClick);
        this._exportButton.removeListener("UserClick", this._onExportClick);
        this._importButton.removeListener("UserClick", this._onImportClick);
        this._addNewRowButton.removeListener("UserClick", this._addNewRowAction);
        this._addNewRedactionRowButton.removeListener("UserClick", this._addNewRedactionRowAction);
        this._slickAllowedTable.onCellChange.unsubscribe(this._onCellChangeAction);
        this._addNewAllowedGridButton.removeListener("UserClick", this._addNewAllowedGridAction);
        this._addNewExclusionButton.removeListener("UserClick", this._addNewExclusionAction);
        //this._resetAllowedGridButton.removeListener("UserClick", this._resetAllowedGridAction);
        this._enableHostNamingCheckBox.removeAllListeners("UserValue");
        this._enableVerboseLogging.removeAllListeners("UserValue");
        document.removeEventListener("click", this._clickHandler);
        window.removeEventListener("resize", this._fitTableSize);
        this._model.removeListener("DataUpdated", this._dataUpdated);
        this._model.removeListener("RdDataUpdated", this._dataUpdatedRd);
        this._model.removeListener("AllowedDataUpdated", this._dataUpdatedAllowed);
        this._model.removeListener("ExclusionsDataUpdated", this._dataUpdateExclusions);
        this._slickTable.onSort.unsubscribe(this._onSort);
        this._slickRdTable.onSort.unsubscribe(this._onSortRedaction);
        this._slickAllowedTable.onSort.unsubscribe(this._onSortAllowed);
        this._model.removeListener("OrgChange", this._orgChange);
    }

    _fitTableSize() {
        if (this._slickTable) {
            this._slickTable.resizeCanvas();
            this._slickRdTable.resizeCanvas();
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this._model) {
            return this._model
                .whenModelIsReady()
                .then(() => {
                    if (this._rootElemContainer) {
                        this._model._showStandardCommands = false;
                        this._model._showDisabledCommands = false;
                        this._rootElemContainer.innerHTML = "";

                        this._rootElemContainer.appendChild(
                            this._createSection(this._discoveryCommandsContainer, {
                                title: "Discovery Commands",
                                bordered: true,
                            })
                        );

                        const headerContainer = document.createElement("div");
                        headerContainer.classList.add("header-elem-container");

                        headerContainer.appendChild(this._commandSetHeaderElem);
                        headerContainer.appendChild(this._checkboxContainer);
                        // const csContainer = document.createDocumentFragment();
                        // csContainer.appendChild(headerContainer);
                        this._discoveryCommandsContainer.appendChild(headerContainer);

                        // Slick Capture Command Sets - START
                        this._columns[2].labelValueMap = this._model._vendorMap;
                        this._discoveryCommandsContainer.appendChild(this._slickWrapper);
                        // csContainer.appendChild(this._slickWrapper);
                        // this._discoveryCommandsContainer.appendChild(this._createSection(csContainer, { title: "Syslog Inbound", bordered: true }));
                        this._toggleNoDataPlaceholder(_.isEmpty(this._model._slickCommandData));
                        this._slickDataVIew = new Slick.Data.DataView();
                        this._slickDataVIew.setItems(this._model._slickCommandData);
                        this._slickTable = new Slick.Grid(
                            this._slickTableContainer,
                            this._slickDataVIew,
                            this._columns,
                            this._tableOptions
                        );
                        this._slickTableContainer.appendChild(
                            this._createNoDataPlaceholder("No Capture Command Sets available")
                        );

                        this._slickWrapper.appendChild(
                            this._uiUtils.wrapElement(
                                this._addNewRowButton.renderer.getRootElement(),
                                null,
                                "add-new-row"
                            )
                        );
                        // Slick Capture Command Sets - END

                        this._discoveryCommandsContainer.appendChild(this._redactionHeaderElem);
                        // Slick Redaction Expressions - START
                        this._discoveryCommandsContainer.appendChild(this._slickRdWrapper);
                        this._toggleRdNoDataPlaceholder(_.isEmpty(this._model._slickRedactionData));
                        this._slickRdDataVIew = new Slick.Data.DataView();
                        this._slickRdDataVIew.setItems(this._model._slickRedactionData);
                        this._slickRdTable = new Slick.Grid(
                            this._slickRdTableContainer,
                            this._slickRdDataVIew,
                            this._columnsRedaction,
                            this._tableOptions
                        );
                        this._slickRdTableContainer.appendChild(
                            this._createNoDataPlaceholder("No Redaction Expressions available")
                        );

                        this._slickRdWrapper.appendChild(
                            this._uiUtils.wrapElement(
                                this._addNewRedactionRowButton.renderer.getRootElement(),
                                null,
                                "add-new-row"
                            )
                        );
                        // Slick Redaction Expressions - END

                        this._rootElemContainer.appendChild(this._createDiscoveryOptsSection());

                        this._rootElemContainer.appendChild(
                            this._createSection(this._slickAllowedWrapper, {
                                title: "Allowed Commands",
                                bordered: true,
                            })
                        );

                        // this._discoveryCommandsContainer.appendChild(this._allowedHeaderElem);
                        // Slick Allowed Expressions - START
                        // this._rootElemContainer.appendChild(this._slickAllowedWrapper);
                        this._toggleAllowedNoDataPlaceholder(
                            _.isEmpty(this._model._slickAllowedData)
                        );
                        this._slickAllowedDataVIew = new Slick.Data.DataView();
                        this._slickAllowedDataVIew.setItems(this._model._slickAllowedData);
                        this._slickAllowedTable = new Slick.Grid(
                            this._slickAllowedTableContainer,
                            this._slickAllowedDataVIew,
                            this._columnsAllowed,
                            this._tableOptions
                        );
                        this._slickAllowedTableContainer.appendChild(
                            this._createNoDataPlaceholder("No Allowed Commands available")
                        );
                        if (this._model.isAuthorizedToManageAllowedCommands()) {
                            /*
                            this._slickAllowedWrapper.appendChild(
                                this._uiUtils.wrapElement(
                                    this._resetAllowedGridButton.renderer.getRootElement(),
                                    null,
                                    "reset-allowed-grid"
                                )
                            );

                             */

                            this._slickAllowedWrapper.appendChild(
                                this._uiUtils.wrapElement(
                                    this._addNewAllowedGridButton.renderer.getRootElement(),
                                    null,
                                    "add-new-row"
                                )
                            );
                        }
                        // Slick Allowed Expressions - END

                        // this._rootElemContainer.appendChild(this._discoveryHeaderElem);

                        this._enableHostNamingCheckBox.setValue(this._model._isHostNamingEnabled);
                        this._enableVerboseLogging.setValue(this._model._isVerboseLoggingEnabled);
                        this._showHideWarningContainer();
                        this._discoveryCommandsContainer.appendChild(this._controlButtonsContainer);

                        this._networkDiscoveryContainer.appendChild(this._exclusionsHeaderElem);
                        this._networkDiscoveryContainer.appendChild(this._slickExclusionsWrapper);
                        this._rootElemContainer.appendChild(
                            this._createSection(this._networkDiscoveryContainer, {
                                title: "Network Discovery",
                                bordered: true,
                            })
                        );

                        this._slickExclusionsDataView = new Slick.Data.DataView();
                        this._slickExclusionsDataView.setItems(
                            this._model._slickExclusionsData || []
                        );
                        this._slickExclusionsTable = new Slick.Grid(
                            this._slickExclusionsTableContainer,
                            this._slickExclusionsDataView,
                            this._exclusionColumns,
                            this._tableOptions
                        );

                        this._slickExclusionsTableContainer.appendChild(
                            this._createNoDataPlaceholder("No Exclusions available")
                        );

                        this._slickExclusionsWrapper.appendChild(
                            this._uiUtils.wrapElement(
                                this._addNewExclusionButton.renderer.getRootElement(),
                                null,
                                "add-new-row"
                            )
                        );

                        if (!this._model.isAuthorizedToManageExclusions()) {
                            this._addNewExclusionButton.setDisabled(true);
                        }

                        this._toggleExclusionsNoDataPlaceholder(
                            _.isEmpty(this._model._slickExclusionsData)
                        );

                        this._slickExclusionsTable.resizeCanvas();
                        this._slickExclusionsTable.invalidateAllRows();
                        this._slickExclusionsTable.render();

                        this._registerListeners();
                        document
                            .querySelector("capture-wrapper .slick-header-column[title='Name']")
                            .click();
                        document
                            .querySelector(
                                "redaction-wrapper .slick-header-column[title='Redaction Name']"
                            )
                            .click();
                        document
                            .querySelector(
                                "allowed-wrapper .slick-header-column[title='Raw Command']"
                            )
                            .click();
                    }
                    return Promise.resolve();
                })
                .catch((err) => console.error(err) || Promise.reject(err));
        }
    }

    _createDiscoveryOptsSection() {
        const _discoveryOptsSection = this._createSection(document.createDocumentFragment(), {
            bordered: true,
            title: "Discovery Options",
        });
        const _discoverySection = document.createDocumentFragment();

        _discoverySection.appendChild(
            this._uiUtils.wrapElement(
                this._enableHostNamingCheckBox.renderer.rootElement,
                "",
                "enable-discovery"
            )
        );
        _discoverySection.appendChild(
            this._uiUtils.wrapElement(
                this._enableVerboseLogging.renderer.rootElement,
                "",
                "enable-verbose"
            )
        );
        const warningContainer = (this._warningContainer = document.createElement("span"));
        warningContainer.classList.add("warning");
        warningContainer.innerHTML =
            '<base-icon-gl data-icon="common.warning"></base-icon-gl>System performance may be impacted';
        this._enableVerboseLogging.renderer.rootElement
            .querySelector("label")
            .appendChild(warningContainer);

        _discoveryOptsSection.appendChild(_discoverySection);

        return _discoveryOptsSection;
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h2");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _dataUpdated() {
        this._toggleNoDataPlaceholder(_.isEmpty(this._model._slickCommandData));
        this._slickDataVIew.setItems(this._model._slickCommandData);
        this._onSort(null, this._sortargs);
    }

    _dataUpdatedRd() {
        this._toggleRdNoDataPlaceholder(_.isEmpty(this._model._slickRedactionData));
        this._slickRdDataVIew.setItems(this._model._slickRedactionData);
        this._onSortRedaction(null, this._sortargsRd);
    }

    _dataUpdatedAllowed() {
        this._toggleAllowedNoDataPlaceholder(_.isEmpty(this._model._slickAllowedData));
        this._slickAllowedDataVIew.setItems(this._model._slickAllowedData);
        this._onSortAllowed(null, this._sortargsAllowed);
        this._slickAllowedTable.resizeCanvas();
        this._slickAllowedTable.invalidate();
        this._slickAllowedTable.render();
    }

    _dataUpdateExclusions() {
        this._toggleExclusionsNoDataPlaceholder(_.isEmpty(this._model._slickExclusionsData));
        this._slickExclusionsDataView.setItems(this._model._slickExclusionsData);
        this._slickExclusionsTable.resizeCanvas();
        this._slickExclusionsTable.invalidate();
        this._slickExclusionsTable.render();
    }

    _onSort(e, args) {
        let col = args.sortCol;
        this._sortargs = args;

        let collator = new Intl.Collator(undefined, {
            numeric: true,
            sensitivity: "base",
        });

        this._slickDataVIew.sort(function (dataRow1, dataRow2) {
            let field = col.field;
            let value1 = dataRow1[field].trim(),
                value2 = dataRow2[field].trim();
            return collator.compare(value1, value2);
        }, args.sortAsc);
        this._slickTable.resizeCanvas();
        this._slickTable.invalidate();
        this._slickTable.render();
    }

    _onSortRedaction(e, args) {
        let col = args.sortCol;
        this._sortargsRd = args;
        let collator = new Intl.Collator(undefined, {
            numeric: true,
            sensitivity: "base",
        });
        this._slickRdDataVIew.sort(function (dataRow1, dataRow2) {
            let field = col.field;
            let value1 = dataRow1[field].trim(),
                value2 = dataRow2[field].trim();
            return collator.compare(value1, value2);
        }, args.sortAsc);
        this._slickRdTable.resizeCanvas();
        this._slickRdTable.invalidate();
        this._slickRdTable.render();
    }

    _onSortAllowed(e, args = this._sortargsAllowed) {
        let col = args.sortCol;
        this._sortargsAllowed = args || {};
        let collator = new Intl.Collator(undefined, {
            numeric: true,
            sensitivity: "base",
        });
        this._slickAllowedDataVIew.sort(function (dataRow1, dataRow2) {
            let field = col.field;
            let value1 = dataRow1[field],
                value2 = dataRow2[field];
            if (dataRow1["allowedName"] === "New Command") {
                return args.sortAsc ? 1 : -1;
            } else if (dataRow2["allowedName"] === "New Command") {
                return args.sortAsc ? -1 : 1;
            }
            return collator.compare(value1, value2);
        }, args.sortAsc);
        this._slickAllowedTable.resizeCanvas();
        this._slickAllowedTable.invalidate();
        this._slickAllowedTable.render();
    }

    _orgChange() {
        this._enableHostNamingCheckBox.setValue(this._model._isHostNamingEnabled);
        this._enableVerboseLogging.setValue(this._model._isVerboseLoggingEnabled);
        this._showHideWarningContainer();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _showHideWarningContainer() {
        if (this._model._isVerboseLoggingEnabled) {
            this._warningContainer.classList.remove("hidden");
        } else {
            this._warningContainer.classList.add("hidden");
        }
    }

    _createNoDataPlaceholder(label) {
        const noDataPlaceholder = document.createElement("div");
        noDataPlaceholder.classList.add("no-data-placeholder");
        noDataPlaceholder.innerText = label || "No Data available";
        return noDataPlaceholder;
    }

    _toggleNoDataPlaceholder(state) {
        this._slickTableContainer.classList.toggle("empty", state);
    }

    _toggleRdNoDataPlaceholder(state) {
        this._slickRdTableContainer.classList.toggle("empty", state);
    }

    _toggleAllowedNoDataPlaceholder(state) {
        this._slickAllowedTableContainer.classList.toggle("empty", state);
    }

    _toggleExclusionsNoDataPlaceholder(state) {
        this._slickExclusionsTableContainer.classList.toggle("empty", state);
    }

    _showAddEditVeil(entity = undefined) {
        const obj = {
            command: this._model._CommandSetEntityDef,
            device: this._model._deviceEntityDef,
            orgId: this._model.activeOrgId,
            ConstructInstance: this._model._constructInstance,
            vendorMap: this._model._vendorMap,
            redactionRegex: this._model._redactionRegexEntityDef,
            valueMap: this._model._valueMapEntityDef,
            muf: this._model._muf,
            parentView: this._model,
            siteEntity: this._model._siteEntity,
            typeBaseMap: this._model._typeBaseMap,
            customFieldEntityClass: this._model._customFieldEntityClass,
            logger: this._model._logger,
        };
        if (entity) {
            Object.assign(obj, {commandEntity: entity});
        }
        const CommandComponent = new CreateCommand(obj);
        return this._model._muf
            .displayCustom(CommandComponent, {notDismissable: false})
            .catch((error) => {
                if (error && error.userRejected === true) {
                    //the veil just has been closed
                    return;
                }
                return error && console.error(error);
            });
    }

    _showExclusionsAddEditVeil(item = {}) {
        const obj = {
            exclusionEntity: this._model._networkDiscoveryExclusionEntity,
            orgId: this._model.activeOrgId,
            muf: this._model._muf,
            item,
            exclusionTypes: this._model._exclusionTypes,
            typeBaseMap: this._model._typeBaseMap,
            parent: this,
            hasPerm: this._model.isAuthorizedToManageExclusions(),
        };
        const createEditExclusionComponent = new createEditExclusion(obj);
        return this._model._muf
            .displayCustom(createEditExclusionComponent, {notDismissable: false})
            .then(() => {
                this._slickExclusionsTable.invalidate();
                this._slickExclusionsTable.render();
                this._toggleExclusionsNoDataPlaceholder(
                    _.isEmpty(this._model._slickExclusionsData)
                );
            })
            .catch((error) => {
                if (error && error.userRejected === true) {
                    //the veil just has been closed
                    return;
                }
                return error && console.error(error);
            });
    }

    _showAddEditRedactionVeil(entity = undefined) {
        const obj = {
            RedactionEntity: this._model._redactionRegexEntityDef,
            orgId: this._model.activeOrgId,
            muf: this._model._muf,
        };
        if (entity) {
            Object.assign(obj, {redactionEntity: entity});
        }
        const RedactionComponent = new CreateEditRedaction(obj);
        return this._model._muf
            .displayCustom(RedactionComponent, {notDismissable: false})
            .catch((error) => {
                if (error && error.userRejected === true) {
                    //the veil just has been closed
                    return;
                }
                return error && console.error(error);
            });
    }

    _onEnableHostNamingCheckBoxChange(value) {
        if (value !== undefined) {
            this._model._isHostNamingEnabled = value;
            this._model._setEnableHostNaming(); //immediate set here since it is a toggle button
        }
    }

    _onEnableVerboseLoggingCheckBoxChange(value) {
        if (value !== undefined) {
            if (value) {
                this._model._muf
                    .displayConfirmation(
                        "Enabling this option may impact the system performance. Are you sure you want to enable verbose logging?",
                        {
                            title: "Verbose Logging",
                            confirmDetails: {
                                text: "Confirm",
                                type: "destructive",
                            },
                            rejectDetails: {
                                text: "Cancel ",
                                type: "text",
                            },
                        }
                    )
                    .then(() => {
                        this._model._isVerboseLoggingEnabled = value;
                        this._model._setVerboseLogging().then(() => {
                            //immediate set here since it is a toggle button
                            this._showHideWarningContainer();
                        });
                    })
                    .catch(() => {
                        this._enableVerboseLogging.setValue(false, false);
                    });
            } else {
                this._model._isVerboseLoggingEnabled = value;
                this._model._setVerboseLogging().then(() => {
                    this._showHideWarningContainer();
                }); //immediate set here since it is a toggle button
            }
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onCellChangeAction(/*e, args*/) {
        this._model.updateAllowedCommands();
    }

    _onRowCountChange(event, {/*previous,*/ current}) {
        this._slickAllowedTable.invalidateAllRows();
        this._slickAllowedTable.resizeCanvas();
        this._toggleAllowedNoDataPlaceholder(!current);
    }

    async _onExportClick() {
        try {
            const workTarget = {};
            const timezone = moment.tz.guess();
            const rawDate = new Date();
            const date = moment.tz(rawDate.toISOString(), timezone).format("YYYYMMDD_HHmmss_z");
            const filename = `CommandSets_${this._model.activeOrgName}_${date}.json`;

            const workResults = await this._model._CommandSetEntityDef.runExportWork(
                this._model.activeOrgId,
                workTarget
            );

            // Check if export was successful
            const errors = workResults.summary?.errors || [];

            // If export failed, display errors in summary veil
            if (errors.length > 0) {
                this._model._muf.displayCustom(
                    new ExportSummaryComponent(errors, this._model._logger)
                );
            }

            const redactionRegexes = workResults.summary?.regex?.export;
            const commandSets = workResults.summary?.command?.export;

            this._fileFactory.saveJsonAsFile(
                JSON.stringify({
                    metadata: {
                        formatType: "command_set",
                        version: "1.0.0",
                    },
                    data: {
                        commandSets,
                        redactionRegexes,
                    },
                }),
                filename
            );
        } catch (err) {
            this._model._logger.error(
                {error: err},
                err,
                "ExportScreenRenderer::_onExportClick - Error"
            );
            this._model._muf.displayError({
                errorMsg: "The command sets cannot be exported.",
                errorCode: "",
            });
        }
    }

    _onImportClick() {
        const importComponent = new ImportComponent(
            this._model._CommandSetEntityDef,
            this._model._logger,
            this._model._io,
            this._model._uiutils,
            this._model._orgId
        );

        this._model._muf
            .displayCustom(importComponent, {
                dontHide: true,
            })
            .then((result) => {
                this._importData = _.get(result, "importData", {});

                this._model._muf.displayLoading({
                    message: "Importing your file... This could take a moment",
                });

                this._importCommandSetNames = Object.values(
                    _.get(result, "importData.data.commandSets", {})
                ).map((cmdSet) => cmdSet.name);

                this._importRegexNames = Object.values(
                    _.get(result, "importData.data.redactionRegexes", {})
                ).map((regex) => regex.name);

                return this._model._CommandSetEntityDef.loadCommandSets(
                    this._model.activeOrgId,
                    {
                        name: {
                            $in: this._importCommandSetNames,
                        },
                    },
                    {}
                );
            })
            .then((result) => {
                const confirmDuplicateHandling = () => {
                    return this._model._muf
                        .displayConfirmation(
                            "Some items already exist in this organization.\r\n\r\n" +
                                "Do you want to update the existing ones or add a copy of them?",
                            {
                                title: "Import",
                                confirmDetails: {
                                    text: "Add",
                                    type: "additive",
                                },
                                rejectDetails: {
                                    text: "Update",
                                    type: "text",
                                },
                            }
                        )
                        .then(() => {
                            return Promise.resolve("rename");
                        })
                        .catch(() => {
                            return Promise.resolve("update");
                        });
                };

                if (result.length > 0) {
                    return confirmDuplicateHandling();
                } else {
                    return this._model._CommandSetEntityDef
                        .loadCommandSets(
                            this._model.activeOrgId,
                            {
                                name: {
                                    $in: this._importRegexNames,
                                },
                            },
                            {}
                        )
                        .then((result) => {
                            if (result.length > 0) {
                                return confirmDuplicateHandling();
                            } else {
                                return Promise.resolve("update");
                            }
                        });
                }
            })
            .then((result) => {
                const duplicateHandlingType = result;

                const getDuplicateHandling = (names, type) => {
                    let obj = {};
                    for (let name of names) {
                        obj[name] = type;
                    }
                    return obj;
                };

                const workTarget = {};

                const workInput = {
                    command: {
                        import: _.get(this._importData, "data.commandSets", {}),
                        duplicateHandling: getDuplicateHandling(
                            this._importCommandSetNames,
                            duplicateHandlingType
                        ),
                    },
                    regex: {
                        import: _.get(this._importData, "data.redactionRegexes", {}),
                        duplicateHandling: getDuplicateHandling(
                            this._importRegexNames,
                            duplicateHandlingType
                        ),
                    },
                };

                const showProgFunc = (prog) => {
                    this._model._muf.showLoadingProgress(
                        `${prog.successCount} of ${prog.targetCount}`
                    );
                };

                return this._model._CommandSetEntityDef.runImportWork(
                    this._model.activeOrgId,
                    workTarget,
                    workInput,
                    {launchAndForget: false},
                    showProgFunc
                );
            })
            .then((result) => {
                return this._model._muf.displayCustom(
                    new ImportSummaryComponent(result, this._logger)
                );
            })
            .catch((err) => {
                if (err.userRejected) {
                    this._model._logger.warn({error: err}, "Import - User Canceled.");
                    return this._model._muf.hideDisplayed();
                } else {
                    this._model._logger.warn({error: err}, "Import Error");
                    return this._model._muf.hideDisplayed().then(() => {
                        this._model._muf.displayError(err).catch(() => {
                            return Promise.resolve();
                        });
                    });
                }
            });
    }

    async _addNewRowAction(/*e, args*/) {
        const allCommandSets = await this._model._CommandSetEntityDef.loadCommandSets(
            this._model.activeOrgId,
            {},
            {},
            {},
            {skip: 0, limit: this._model._maxPageSize}
        );

        if (allCommandSets && allCommandSets.length < this._model._maxPageSize) {
            return this._showAddEditVeil();
        }

        this._model._muf.displayNotification(
            `Sorry, you've reached the limit of ${this._model._maxPageSize} Command Sets. Please delete some before adding new ones.`,
            {
                notDismissable: true,
                title: "Add Capture Command Set",
                confirmDetails: {text: "OK"},
            }
        );
    }

    async _addNewRedactionRowAction() {
        const allRedactionRegexes = await this._model._redactionRegexEntityDef.loadRedactionRegexes(
            this._model.activeOrgId,
            {},
            {},
            {},
            {skip: 0, limit: this._model._maxPageSize}
        );

        if (allRedactionRegexes && allRedactionRegexes.length < this._model._maxPageSize) {
            return this._showAddEditRedactionVeil();
        }

        this._model._muf.displayNotification(
            `Sorry, you've reached the limit of ${this._model._maxPageSize} Redaction Expressions. Please delete some before adding new ones.`,
            {
                notDismissable: true,
                title: "Add Redaction Expression",
                confirmDetails: {text: "OK"},
            }
        );
    }

    _addNewAllowedGridAction() {
        const newCommand = {
            id: "NewCommand" + this._model._slickAllowedData.length,
            allowedName: "New Command",
            group1: true,
            group2: false,
            nonCapture: false,
        };

        this._model._slickAllowedData.push(newCommand);

        this._model.updateAllowedCommands();
    }

    /*
    _resetAllowedGridAction() {
        this._model.resetAllowedCommands();
    }

     */

    _addNewExclusionAction() {
        // Add exclusion veil
        this._showExclusionsAddEditVeil();
    }

    _clickHandler(e) {
        //if we click delete column
        const target = e.target;
        if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "delete" &&
            target.dataset.uuid
        ) {
            this._deleteItem(target.dataset.uuid);
        } else if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "edit" &&
            target.dataset.uuid
        ) {
            this._editItem(target.dataset.uuid);
        } else if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "clone" &&
            target.dataset.uuid
        ) {
            this._cloneItem(target.dataset.uuid);
        } else if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "deleteR" &&
            target.dataset.uuid
        ) {
            this._deleteRedactionItem(target.dataset.uuid);
        } else if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "editR" &&
            target.dataset.uuid
        ) {
            this._editRedactionItem(target.dataset.uuid);
        } else if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "deleteA" &&
            target.dataset.uuid
        ) {
            this._deleteAllowedItem(target.dataset.uuid);
        } else if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "deleteEx" &&
            target.dataset.uuid
        ) {
            this._deleteExclusionItem(target.dataset.uuid);
        } else if (
            target.getAttribute("role") === "button" &&
            target.dataset.action === "editEx" &&
            target.dataset.uuid
        ) {
            this._editExclusionItem(target.dataset.uuid);
        }

        this._saveAndCloseEditCellAction(e);
    }

    _saveAndCloseEditCellAction(e) {
        //do nothing if no active editors
        if (!this._slickAllowedTable.getEditorLock().isActive()) {
            return false;
        }

        //get event bubbling path
        const path = e.path || e.composedPath();

        //check if click was outside of current editing cell
        let outsideClick = true;
        _.each(path, (element) => {
            const elHasClass = (className) =>
                element.classList && element.classList.contains(className);

            if (elHasClass("slick-cell")) {
                const cellHasActiveEditor = elHasClass("active") && elHasClass("editable");
                outsideClick = !cellHasActiveEditor;
                return false;
            }

            if (element === this._slickTableContainer) {
                return false;
            }
        });

        if (outsideClick) {
            this._slickTable.getEditorLock().commitCurrentEdit();
        }
    }

    _deleteAllowedItem(target) {
        const item = this._slickAllowedDataVIew.getItemById(target);
        const deleteConfirmation = (item) => {
            const deletedItemName = _.get(item, "allowedName", "Unknown");
            const deleteMessage = `Are you sure you want to delete "${deletedItemName}"?`;
            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };

        return deleteConfirmation(item)
            .then(() => {
                const removeCommand = (value, index) => {
                    if (value.id === target) {
                        this._model._slickAllowedData.splice(index, 1);
                        return true;
                    }
                };
                this._model._slickAllowedData.filter(removeCommand);
                //this._model._slickAllowedData.splice(target, 1);
                this._model.updateAllowedCommands();
            })
            .catch((error) => {
                this._model._logger.warn(
                    {error: error},
                    "CaptureRenderer.deleteAllowedConfirmation() - User Canceled"
                );
                return Promise.resolve();
            });
    }

    _deleteItem(target) {
        const item = this._slickDataVIew.getItemById(target);
        const deleteConfirmation = (item) => {
            const deletedItemName = _.get(item, "commandName", "Unknown");
            const deleteMessage = `Are you sure you want to delete "${deletedItemName}"?`;
            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };
        return deleteConfirmation(item)
            .then(() => {
                const targetEntity = this._model._commandSetsFeed.getInstanceById(target);
                return targetEntity.delete();
            })
            .catch((error) => {
                if (error) {
                    this._model._logger.warn(
                        {error: error},
                        "CaptureRenderer.deleteConfirmation() - Error"
                    );
                    const errorMessage = error.errorMsg.replace(
                        "An error occurred while attempting to run work in a transaction: ",
                        ""
                    );
                    return this._model._muf
                        .displayError({errorMsg: errorMessage, errorCode: error.errorCode})
                        .catch(() => {
                            return Promise.resolve();
                        });
                } else {
                    this._model._logger.warn(
                        {error: error},
                        "CaptureRenderer.deleteConfirmation() - User Canceled"
                    );
                    return Promise.resolve();
                }
            });
    }

    _deleteExclusionItem(target) {
        const item = this._slickExclusionsDataView.getItemById(target);
        const deleteConfirmation = () => {
            const itemVal =
                item.exType === "ipRange" && item.value
                    ? item.value.start + " - " + item.value.end
                    : item.exType === "deviceType" && item.value
                    ? this._model._typeBaseMap[item.value]
                    : item.value;

            // const deletedItemName = _.get(item, "commandName", "Unknown");
            const deleteMessage = `Are you sure you want to remove the exclusion of the ${item.type} ${itemVal}?`;
            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };
        return deleteConfirmation(item).then(() => {
            this._slickExclusionsDataView.deleteItem(target);
            this._slickExclusionsTable.resizeCanvas();
            this._slickExclusionsTable.invalidateAllRows();
            this._slickExclusionsTable.render();
            this._toggleExclusionsNoDataPlaceholder(_.isEmpty(this._model._slickExclusionsData));
            const exclusions = this._slickExclusionsDataView.getItems().map((item) => {
                const ex = {};
                ex[item.exType] = item.value;
                ex["description"] = item.description;
                return ex;
            });
            this._model._networkDiscoveryExclusionEntity.setExclusions(exclusions);
            this._model._networkDiscoveryExclusionEntity.update().catch((err) => {
                console.log(err);
            });
        });
    }

    _editExclusionItem(target) {
        const item = this._slickExclusionsDataView.getItemById(target);
        this._showExclusionsAddEditVeil(item);
    }

    _editItem(target) {
        const targetEntity = this._model._commandSetsFeed.getInstanceById(target);
        this._showAddEditVeil(targetEntity);
    }

    _cloneItem(target) {
        const targetEntity = this._model._commandSetsFeed.getInstanceById(target);

        if (
            targetEntity.getBasicMode() &&
            _.isEmpty(targetEntity.getCommands()) &&
            _.isEmpty(targetEntity.getConceptItemIds()) &&
            _.isEmpty(targetEntity.getRsiIds()) &&
            _.isEmpty(targetEntity.getGetUrls())
        ) {
            return this._model._muf
                .displayError({
                    errorMsg:
                        "A command set with only Gluware standard commands enabled cannot be cloned.",
                })
                .catch(() => {
                    return Promise.resolve();
                });
        }

        return this._generateName(targetEntity.getName())
            .then((name) => {
                return targetEntity.clone(name);
            })
            .then((clonedEntity) => {
                if (clonedEntity.getBasicMode()) {
                    clonedEntity.setBasicMode(false);
                    return clonedEntity.update({skipHooks: ["auditPolicy"]});
                }
            })
            .catch((error) => {
                this._model._logger.warn({error: error}, "CaptureRenderer._cloneItem() - Error");
                return this._model._muf.displayError(error).catch(() => {
                    return Promise.resolve();
                });
            });
    }

    async _generateName(name) {
        const baseName = this._removeTrailingInteger(name);
        let existingNames = [baseName];
        let count = 2;
        let newName = baseName;

        const allCommandSets = await this._model._CommandSetEntityDef.loadCommandSets(
            this._model.activeOrgId,
            {name: {$regex: `^${baseName} [0-9]+$`}},
            {name: true},
            {},
            {skip: 0, limit: this._model._maxPageSize}
        );
        allCommandSets.forEach((commandSetEntity) => {
            existingNames.push(commandSetEntity.name);
        });
        while (existingNames.includes(newName)) {
            newName = `${baseName} ${count}`;
            count++;
        }
        return newName;
    }

    _removeTrailingInteger(str) {
        let words = str.split(" ");
        if (!isNaN(words[words.length - 1])) {
            words.pop();
        }
        return words.join(" ");
    }

    _editRedactionItem(target) {
        const targetEntity = this._model._redactionsFeed.getInstanceById(target);
        this._showAddEditRedactionVeil(targetEntity);
    }

    _deleteRedactionItem(target) {
        const item = this._slickRdDataVIew.getItemById(target);
        const deleteConfirmation = (item) => {
            const deletedItemName = _.get(item, "redactionName", "Unknown");
            const deleteMessage = `Are you sure you want to delete "${deletedItemName}"?`;
            return this._uiUtils.showDeleteConfirmDialog({
                message: deleteMessage,
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
            });
        };
        return deleteConfirmation(item)
            .then(() => {
                const targetEntity = this._model._redactionsFeed.getInstanceById(target);
                return targetEntity.delete();
            })
            .catch(() => {
                console.log("User Rejected");
            });
    }

    // _showStandardSetsClick(val) {
    //     this._model._showStandardCommands = val;
    //     this._model._getAllCommandSets();
    // }

    _showDisabledSetsClick(val) {
        this._model._showDisabledCommands = val;
        this._model._getAllCommandSets();
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
}

module.exports = CaptureRenderer;
